import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardMedia, Typography } from '@mui/material';
import { Distance, Duration, IAverageRating } from '@lialo/core/lib/models';
import { formatMoney } from '@lialo/common/lib/formatter/money';
import { AverageTourRating } from '../TourDetail/TourRatings/AverageTourRating';
import { STAR_SIZE_SMALL } from '../../../common/StarRating';

const PREFIX = 'TourCheckoutSummary';

const classes = {
  card: `${PREFIX}-card`,
  headline: `${PREFIX}-headline`,
  responsive: `${PREFIX}-responsive`,
  img: `${PREFIX}-img`,
  cardContent: `${PREFIX}-cardContent`,
  price: `${PREFIX}-price`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.card}`]: {
    display: 'flex',
    backgroundColor: 'transparent',
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.headline}`]: {
    fontWeight: theme.typography.fontWeightBold,
  },

  [`& .${classes.responsive}`]: {
    maxWidth: 250,
    width: '33%',
  },

  [`& .${classes.img}`]: {
    paddingTop: '75%',
    width: '100%',
    borderRadius: theme.spacing(1),
  },

  [`& .${classes.cardContent}`]: {
    padding: theme.spacing(0, 0, 0, 2),
    '&:last-child': {
      paddingBottom: 0,
    },
  },

  [`& .${classes.price}`]: {
    fontSize: '1rem',
  },
}));

interface Props {
  imageSrc?: string;
  title: string;
  duration?: Duration | null;
  distance?: Distance | null;
  numberOfStops: number;
  numberOfTasks: number;
  price: string;
  averageRating: IAverageRating;
}

const TourCheckoutSummary = ({
  imageSrc,
  title,
  duration,
  distance,
  numberOfStops,
  numberOfTasks,
  price,
  averageRating,
}: Props) => {
  const { t } = useTranslation();

  return (
    <StyledCard elevation={0} className={classes.card}>
      {imageSrc && (
        <div className={classes.responsive}>
          <CardMedia className={classes.img} image={imageSrc} />
        </div>
      )}
      <CardContent className={classes.cardContent}>
        <Typography className={classes.headline} variant={'subtitle1'} component={'h2'}>
          {title}
        </Typography>
        <AverageTourRating
          totalRatings={averageRating.ratingCount}
          value={averageRating.averageRating ?? 0}
          starSize={STAR_SIZE_SMALL}
        />

        <Typography variant={'caption'} color={'textSecondary'} component={'div'}>
          {t('tour_detail.checkout.summary.description_alt')}
          {/*
          temporarily reduced to two description_alt
          {t('tour_detail.checkout.summary.description_1')}
          <br />
          {t('tour_detail.checkout.summary.description_2')}
          <br />
          {t('tour_detail.checkout.summary.description_3')}
          <br />
          {duration && t(`tour_detail.checkout.summary.duration.${duration}`) + ', '}
          {distance && t(`tour_detail.checkout.summary.distance.${distance}`) + ', '}
          {t('tour_detail.checkout.summary.stops', { count: numberOfStops })}
          {', '}
          {t('tour_detail.checkout.summary.tasks', { count: numberOfTasks })}
          */}
        </Typography>

        <Typography className={classes.price} variant={'subtitle2'}>
          {formatMoney(price)}
        </Typography>
      </CardContent>
    </StyledCard>
  );
};

export default TourCheckoutSummary;
